/** Shared by EventQ and Resolve on the same site. Resolve reads what EventQ appends. */
export const TALKDOWN_ATTEMPTS_STORAGE_KEY = 'eventq:talkdown-attempts';

export interface StoredTalkdownAttempt {
  talkdownId: number;
  succeeded: boolean;
}

type StoredMap = Record<string, StoredTalkdownAttempt[]>;

function isAttempt(value: unknown): value is StoredTalkdownAttempt {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const attempt = value as StoredTalkdownAttempt;
  return (
    Number.isInteger(attempt.talkdownId) &&
    attempt.talkdownId > 0 &&
    typeof attempt.succeeded === 'boolean'
  );
}

function readMap(): StoredMap {
  try {
    const raw = localStorage.getItem(TALKDOWN_ATTEMPTS_STORAGE_KEY);
    if (!raw) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return {};
    }
    const result: StoredMap = {};
    for (const [eventId, attempts] of Object.entries(parsed)) {
      if (!Array.isArray(attempts)) {
        continue;
      }
      const valid = attempts.filter(isAttempt);
      if (valid.length > 0) {
        result[eventId] = valid;
      }
    }
    return result;
  } catch {
    return {};
  }
}

function writeMap(state: StoredMap): void {
  try {
    if (Object.keys(state).length === 0) {
      localStorage.removeItem(TALKDOWN_ATTEMPTS_STORAGE_KEY);
      return;
    }
    localStorage.setItem(TALKDOWN_ATTEMPTS_STORAGE_KEY, JSON.stringify(state));
  } catch {
    // Keep the previous list when storage is blocked or full.
  }
}

export function readTalkdownAttempts(eventId: string): StoredTalkdownAttempt[] {
  if (!eventId.trim()) {
    return [];
  }
  return readMap()[eventId] ?? [];
}

export function appendTalkdownAttempt(eventId: string, attempt: StoredTalkdownAttempt): void {
  if (!eventId.trim() || !isAttempt(attempt)) {
    return;
  }
  const state = readMap();
  state[eventId] = [...(state[eventId] ?? []), { talkdownId: attempt.talkdownId, succeeded: attempt.succeeded }];
  writeMap(state);
}

export function clearTalkdownAttempts(eventId: string): void {
  if (!eventId.trim()) {
    return;
  }
  const state = readMap();
  if (!(eventId in state)) {
    return;
  }
  delete state[eventId];
  writeMap(state);
}
